import type {CustomPropertyDetailsPagePayload, PropertyDefinition} from '@github-ui/custom-properties-types'
import {propertyDefinitionsListPath} from '@github-ui/paths'
import {Link} from '@github-ui/react-core/link'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {Box, Button} from '@primer/react'
import {createRef, useState} from 'react'

import {ServerErrorFormBanner} from '../components/Banners'
import {DeleteDefinitionDialog} from '../components/DeleteDefinitionDialog'
import {PageHeader} from '../components/PageHeader'
import {PropertyDefinitionSettings} from '../components/PropertyDefinitionSettings'
import {CurrentOrgRepoProvider, useCurrentOrg} from '../contexts/CurrentOrgRepoContext'

export function CustomPropertyDetailsPage() {
  const {propertyNames, definition} = useRoutePayload<CustomPropertyDetailsPagePayload>()
  const [formError, setFormError] = useState('')

  return (
    <CurrentOrgRepoProvider>
      {formError && (
        <Box sx={{mb: 3}}>
          <ServerErrorFormBanner>{formError}</ServerErrorFormBanner>
        </Box>
      )}

      <Header definition={definition} />

      <PropertyDefinitionSettings
        definition={definition}
        existingPropertyNames={propertyNames}
        setFormError={setFormError}
      />
    </CurrentOrgRepoProvider>
  )
}

interface HeaderProps {
  definition?: PropertyDefinition
}
function Header({definition}: HeaderProps) {
  const org = useCurrentOrg()
  const [deleteDialogIsOpen, setDeleteDialogIsOpen] = useState(false)
  const contextMenuButtonRef = createRef<HTMLButtonElement>()

  function closeDeleteDialog() {
    setDeleteDialogIsOpen(false)
    contextMenuButtonRef.current?.focus()
  }

  return (
    <>
      <PageHeader
        title={
          <Box as="h2" sx={{fontWeight: 400}}>
            <Link to={propertyDefinitionsListPath({org: org.login})}>Repository properties</Link> /{' '}
            {definition?.propertyName || 'New property'}
          </Box>
        }
        actions={
          definition && (
            <Button
              ref={contextMenuButtonRef}
              variant="danger"
              sx={{ml: 3}}
              onClick={() => setDeleteDialogIsOpen(true)}
            >
              Delete property
            </Button>
          )
        }
      />

      {definition && deleteDialogIsOpen && (
        <DeleteDefinitionDialog definition={definition} onCancel={closeDeleteDialog} onDismiss={closeDeleteDialog} />
      )}
    </>
  )
}

try{ CustomPropertyDetailsPage.displayName ||= 'CustomPropertyDetailsPage' } catch {}
try{ Header.displayName ||= 'Header' } catch {}